import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { STAGES } from "@/lib/constants";
import { StageBadge, StatusBadge } from "@/components/Badges";
import { fmtDate, fmtDateTime, isoToLocalInput, localInputToMelbourneISO, buildGCalLink } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, Rocket, UserX, RotateCcw, Save, CalendarPlus, ExternalLink,
  Loader2, CheckCircle2, Trash2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function PBODetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewAs, user: currentUser } = useAuth();
  const [pbo, setPbo] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [confirmDrop, setConfirmDrop] = useState(false);
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const [dropReason, setDropReason] = useState("");
  const [launchCoaches, setLaunchCoaches] = useState([]);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeForm, setCompleteForm] = useState({ stage: "", coaches: [], completed_at: "", next_stage: "", next_meeting_at: "" });

  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ stage: "", at: "" });

  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", partner_name: "" });

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");
  const canPermanentDelete = currentUser?.role === "admin" && !isViewAs;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pbos/${id}`);
      setPbo(data);
      setEditForm({
        name: data.name || "", partner_name: data.partner_name || "",
      });
    } catch { toast.error("Could not load PBO"); navigate("/pbos"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // eslint-disable-next-line
  }, [id]);
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  useEffect(() => {
    const params = pbo?.upline_team ? { team: pbo.upline_team } : {};
    api.get("/leaders", { params }).then((r) => setLeaders(r.data || []));
  }, [pbo?.upline_team]);

  if (loading || !pbo) return <div className="text-sm text-slate-500">Loading…</div>;

  const currentIdx = STAGES.indexOf(pbo.current_stage);
  const nextStage = pbo.status === "Active" && currentIdx >= 0 && currentIdx < STAGES.length - 1
    ? STAGES[currentIdx + 1] : null;

  const openComplete = (stage) => {
    setCompleteForm({
      stage,
      coaches: pbo.next_meeting?.stage === stage ? [] : [],
      completed_at: pbo.next_meeting?.stage === stage ? isoToLocalInput(pbo.next_meeting?.at) : isoToLocalInput(new Date().toISOString()),
      next_stage: (() => {
        const idx = STAGES.indexOf(stage);
        return idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1] : "";
      })(),
      next_meeting_at: "",
    });
    setCompleteOpen(true);
  };

  const submitComplete = async () => {
    setSaving(true);
    try {
      const payload = {
        stage: completeForm.stage,
        completed_at: completeForm.completed_at ? localInputToMelbourneISO(completeForm.completed_at) : null,
        coaches: completeForm.coaches,
        next_meeting: completeForm.next_meeting_at && completeForm.next_stage
          ? { stage: completeForm.next_stage, at: localInputToMelbourneISO(completeForm.next_meeting_at) }
          : null,
      };
      const { data } = await api.post(`/pbos/${id}/complete-stage`, payload);
      setPbo(data);
      toast.success(`Marked ${completeForm.stage} complete`);
      setCompleteOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const doLaunch = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pbos/${id}/launch`, { coaches: launchCoaches });
      setPbo(data); toast.success("Marked as Launched");
      setConfirmLaunch(false); setLaunchCoaches([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const doDropout = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pbos/${id}/dropout`, { reason: dropReason });
      setPbo(data); toast.success("Marked as Dropped Out");
      setConfirmDrop(false); setDropReason("");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const doReactivate = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pbos/${id}/reactivate`);
      setPbo(data); toast.success("Reactivated");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const saveMeeting = async () => {
    setSaving(true);
    try {
      const nm = meetingForm.at && meetingForm.stage
        ? { stage: meetingForm.stage, at: localInputToMelbourneISO(meetingForm.at) }
        : null;
      const { data } = nm
        ? await api.patch(`/pbos/${id}`, { next_meeting: nm })
        : await api.patch(`/pbos/${id}`, { clear_next_meeting: true });
      setPbo(data);
      toast.success(nm ? "Meeting scheduled" : "Meeting cleared");
      setMeetingOpen(false);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/pbos/${id}`, editForm);
      setPbo(data); toast.success("Saved"); setEditing(false);
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const toggleChecklist = async (name, done) => {
    try {
      const { data } = await api.post(`/pbos/${id}/checklist`, { name, done });
      setPbo(data);
    } catch { toast.error("Failed"); }
  };

  const openMeeting = () => {
    setMeetingForm({
      stage: pbo.next_meeting?.stage || (nextStage || "MG2/BP1"),
      at: isoToLocalInput(pbo.next_meeting?.at),
    });
    setMeetingOpen(true);
  };

  const gcalHref = buildGCalLink({
    title: `ATL ${pbo.next_meeting?.stage || ""} · ${pbo.name}`.trim(),
    details: `Stage: ${pbo.next_meeting?.stage || pbo.current_stage}\nIBO/Partner: ${pbo.ibo_partner_name || "—"}\nTeam: ${pbo.upline_team || "—"}`,
    startISO: pbo.next_meeting?.at,
    durationMinutes: 60,
  });

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="pbo-detail-page">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="text-sm text-slate-500 inline-flex items-center gap-1 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-[#0A1F44] text-white rounded-2xl p-5 sm:p-6 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full border border-[#C9A961]/20" />
        <div className="absolute -right-4 -bottom-16 w-40 h-40 rounded-full border border-[#C9A961]/10" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">PBO</div>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1">
            {pbo.name}{pbo.partner_name ? <span className="text-white/70 text-2xl"> & {pbo.partner_name}</span> : null}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <StageBadge stage={pbo.current_stage} />
            <StatusBadge status={pbo.status} />
            {pbo.is_demo && <span className="text-[10px] uppercase tracking-wider bg-white/10 text-white/70 px-2 py-0.5 rounded">demo</span>}
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            <Info label="IBO / Partner" value={pbo.ibo_partner_name} />
            <Info label="Upline Team" value={pbo.upline_team} />
            <Info label="Nearest upline" value={pbo.nearest_upline_leader_name || "—"} />
            <Info label="Entered" value={fmtDate(pbo.date_entered)} />
            {pbo.status === "Launched" && <Info label="Launched" value={fmtDate(pbo.launch_date)} />}
            {pbo.status === "Dropped Out" && <Info label="Dropped" value={fmtDate(pbo.dropout_date)} />}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {isViewAs && (
          <div className="text-xs text-slate-500 italic px-2 py-1.5" data-testid="view-as-readonly-note">
            Read-only preview — actions disabled while viewing as another user.
          </div>
        )}
        {!isViewAs && pbo.status === "Active" && nextStage && (
          <Button data-testid="complete-stage-btn" disabled={saving} onClick={() => openComplete(nextStage)} className="rounded-full bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Mark {nextStage} complete
          </Button>
        )}
        {!isViewAs && pbo.status === "Active" && (
          <Button data-testid="launch-btn" variant="outline" disabled={saving} onClick={() => setConfirmLaunch(true)} className="rounded-full border-[#C9A961] text-[#7A5A0C] hover:bg-[#FBF3DC]">
            <Rocket className="w-4 h-4 mr-1" /> Launch
          </Button>
        )}
        {!isViewAs && pbo.status !== "Dropped Out" && pbo.status === "Active" && (
          <Button data-testid="dropout-btn" variant="outline" disabled={saving} onClick={() => setConfirmDrop(true)} className="rounded-full border-[#8A1F1F]/40 text-[#8A1F1F] hover:bg-[#FBECEC]">
            <UserX className="w-4 h-4 mr-1" /> Dropped Out
          </Button>
        )}
        {!isViewAs && pbo.status !== "Active" && (
          <Button data-testid="reactivate-btn" variant="outline" disabled={saving} onClick={doReactivate} className="rounded-full">
            <RotateCcw className="w-4 h-4 mr-1" /> Reactivate
          </Button>
        )}
        {!isViewAs && pbo.status === "Active" && (
          <Button data-testid="schedule-meeting-btn" variant="outline" onClick={openMeeting} className="rounded-full">
            <CalendarPlus className="w-4 h-4 mr-1" /> {pbo.next_meeting?.at ? "Edit next meeting" : "Schedule next meeting"}
          </Button>
        )}
        {canPermanentDelete && (
          <Button
            data-testid="permanent-delete-btn"
            variant="outline"
            onClick={() => { setDeleteTyped(""); setDeleteOpen(true); }}
            className="rounded-full ml-auto border-[#8A1F1F]/50 text-[#8A1F1F] hover:bg-[#FBECEC]"
          >
            <Trash2 className="w-4 h-4 mr-1" /> Permanently delete
          </Button>
        )}
      </div>

      {/* Next meeting */}
      <Card className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#C9A961] font-semibold">Next appointment</div>
            <div className="font-serif text-xl text-[#0A1F44] mt-1 flex items-center gap-2" data-testid="next-meeting-display">
              {pbo.next_meeting?.at ? (
                <>
                  <StageBadge stage={pbo.next_meeting.stage} />
                  <span>{fmtDateTime(pbo.next_meeting.at)}</span>
                </>
              ) : "None scheduled"}
            </div>
          </div>
          {pbo.next_meeting?.at && (
            <a href={gcalHref} target="_blank" rel="noreferrer" data-testid="gcal-link"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-[#C9A961] text-[#0A1F44] hover:bg-[#B69248] font-medium">
              <ExternalLink className="w-4 h-4" /> Add to Google Calendar
            </a>
          )}
        </div>
      </Card>

      {/* Details / edit */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg text-[#0A1F44]">Details</h2>
          {isViewAs ? null : !editing ? (
            <Button data-testid="edit-btn" size="sm" variant="outline" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); load(); }}>Cancel</Button>
              <Button data-testid="save-edit-btn" size="sm" onClick={saveEdit} disabled={saving} className="bg-[#0A1F44] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
              </Button>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="space-y-2 text-sm">
            <div className="text-slate-500 text-xs uppercase tracking-wider">Name</div>
            <div className="text-slate-800" data-testid="pbo-detail-name">{pbo.name}{pbo.partner_name ? ` & ${pbo.partner_name}` : ""}</div>
            {pbo.has_legacy_private_fields && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="pbo-legacy-private-note">
                This record has older contact details or notes on file. They're hidden and held privately pending an Admin-reviewed clean-up.
              </div>
            )}
            {pbo.status === "Dropped Out" && pbo.dropout_reason && (
              <div className="pt-2">
                <div className="text-slate-500 text-xs uppercase tracking-wider">Dropout reason</div>
                <div className="text-slate-800">{pbo.dropout_reason}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name"><Input data-testid="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></F>
            <F label="Partner name"><Input data-testid="edit-partner" value={editForm.partner_name} onChange={(e) => setEditForm({ ...editForm, partner_name: e.target.value })} /></F>
            <p className="sm:col-span-2 text-xs text-slate-500" data-testid="edit-privacy-hint">To protect PBO privacy, only names and appointment progress are recorded — no contact details or personal notes.</p>
          </div>
        )}
      </Card>

      {(pbo.status === "Launched" || pbo.current_stage === "Post Launch") && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-lg text-[#0A1F44]">Post-Launch checklist</h2>
            <div className="text-xs text-slate-500">
              {(pbo.checklist || []).filter((c) => c.done).length} / {(pbo.checklist || []).length} complete
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-2" data-testid="checklist">
            {(pbo.checklist || []).map((c) => (
              <label key={c.name} className={`flex items-center justify-between gap-3 px-3 py-2 border rounded-lg ${isViewAs ? "cursor-not-allowed opacity-90" : "cursor-pointer"} ${c.done ? "bg-[#EAF6EE] border-[#B7DFC4]" : "bg-white border-slate-200"}`}>
                <div className="flex items-center gap-3">
                  <Checkbox checked={c.done} onCheckedChange={(v) => !isViewAs && toggleChecklist(c.name, !!v)} disabled={isViewAs} data-testid={`chk-${c.name}`} />
                  <span className="text-sm text-slate-800">{c.name}</span>
                </div>
                {c.done && c.completed_at && <span className="text-[10px] text-slate-500">{fmtDate(c.completed_at)}</span>}
              </label>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5">
        <h2 className="font-serif text-lg text-[#0A1F44] mb-3">Stage history</h2>
        <ol className="relative border-l border-slate-200 pl-4 space-y-3">
          {(pbo.stage_history || []).slice().reverse().map((h, i) => (
            <li key={i} className="text-sm">
              <span className="absolute -left-1.5 mt-1.5 w-2.5 h-2.5 rounded-full bg-[#C9A961] border-2 border-white" />
              <div className="flex flex-wrap items-center gap-2">
                <StageBadge stage={h.stage} />
                <span className="text-slate-500 text-xs">{fmtDateTime(h.entered_at)}</span>
                {(h.coaches || []).length > 0 && (
                  <span className="text-xs text-slate-700">
                    · Coach{h.coaches.length > 1 ? "es" : ""}: {(h.coaches || []).join(", ")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </Card>

      {/* Complete-stage dialog */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0A1F44]">Mark {completeForm.stage} complete</DialogTitle>
            <DialogDescription>Record when the appointment happened and which coach/es were on it. The next appointment can be scheduled at the same time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <F label="Completed at (Melbourne)">
              <Input type="datetime-local" data-testid="complete-at" value={completeForm.completed_at} onChange={(e) => setCompleteForm({ ...completeForm, completed_at: e.target.value })} />
            </F>
            <div>
              <Label className="text-sm text-slate-700">Coach/es on this appointment</Label>
              <div className="mt-2 max-h-56 overflow-y-auto grid gap-1.5" data-testid="complete-coaches">
                {leaders.map((c) => {
                  const checked = completeForm.coaches.includes(c.name);
                  return (
                    <label key={c.id} className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer ${checked ? "bg-[#0A1F44]/5 border-[#0A1F44]/30" : "bg-white border-slate-200"}`}>
                      <Checkbox checked={checked} onCheckedChange={() => setCompleteForm((f) => ({
                        ...f, coaches: f.coaches.includes(c.name) ? f.coaches.filter((x) => x !== c.name) : [...f.coaches, c.name],
                      }))} />
                      <span>{c.name}</span>
                      <span className="text-[10px] text-slate-400 ml-auto uppercase">{c.role}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-1">
              <F label="Next stage (optional)">
                <Select value={completeForm.next_stage} onValueChange={(v) => setCompleteForm({ ...completeForm, next_stage: v })}>
                  <SelectTrigger><SelectValue placeholder="No follow-up scheduled" /></SelectTrigger>
                  <SelectContent>
                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Next date/time (Melbourne)">
                <Input type="datetime-local" data-testid="complete-next-at" value={completeForm.next_meeting_at} onChange={(e) => setCompleteForm({ ...completeForm, next_meeting_at: e.target.value })} />
              </F>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button data-testid="complete-submit" onClick={submitComplete} disabled={saving} className="bg-[#0A1F44] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Launch dialog */}
      <AlertDialog open={confirmLaunch} onOpenChange={setConfirmLaunch}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {pbo.name} as Launched?</AlertDialogTitle>
            <AlertDialogDescription>Sets status to Launched, records the launch date, moves stage to Post Launch, and captures who coached the launch.</AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="text-sm text-slate-700">Launch coach/es (optional)</Label>
            <div className="mt-2 max-h-40 overflow-y-auto grid gap-1.5">
              {leaders.map((c) => {
                const checked = launchCoaches.includes(c.name);
                return (
                  <label key={c.id} className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg text-sm cursor-pointer ${checked ? "bg-[#0A1F44]/5 border-[#0A1F44]/30" : "bg-white border-slate-200"}`}>
                    <Checkbox checked={checked} onCheckedChange={() => setLaunchCoaches((l) => l.includes(c.name) ? l.filter((x) => x !== c.name) : [...l, c.name])} />
                    <span>{c.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="launch-confirm" onClick={doLaunch} className="bg-[#0A1F44]">Confirm Launch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dropout dialog */}
      <AlertDialog open={confirmDrop} onOpenChange={setConfirmDrop}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {pbo.name} as Dropped Out?</AlertDialogTitle>
            <AlertDialogDescription>This is a significant status change. History is retained. Add an optional reason:</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea data-testid="dropout-reason" placeholder="Reason (optional) — process reason only, no personal details" value={dropReason} onChange={(e) => setDropReason(e.target.value)} rows={3} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="dropout-confirm" onClick={doDropout} className="bg-[#8A1F1F] hover:bg-[#701818]">Confirm Dropout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Meeting dialog */}
      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]">Schedule next appointment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Stage">
              <Select value={meetingForm.stage} onValueChange={(v) => setMeetingForm({ ...meetingForm, stage: v })}>
                <SelectTrigger data-testid="meeting-stage"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Date & time (Melbourne)">
              <Input type="datetime-local" data-testid="meeting-input" value={meetingForm.at} onChange={(e) => setMeetingForm({ ...meetingForm, at: e.target.value })} />
            </F>
          </div>
          <DialogFooter className="gap-2">
            {pbo.next_meeting?.at && <Button variant="ghost" onClick={() => setMeetingForm({ ...meetingForm, at: "" })}>Clear</Button>}
            <Button variant="outline" onClick={() => setMeetingOpen(false)}>Cancel</Button>
            <Button data-testid="meeting-save" onClick={saveMeeting} disabled={saving} className="bg-[#0A1F44] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent delete (admin only) */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this PBO?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <span className="font-medium">{pbo.name}</span> and its entire stage history from the database and every report. It is not a dropout — it will not appear in launched, dropped or conversion figures. This cannot be undone.
              <br />Type <span className="font-mono font-bold">DELETE</span> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            data-testid="permanent-delete-input"
            value={deleteTyped}
            onChange={(e) => setDeleteTyped(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="permanent-delete-confirm"
              onClick={async () => {
                if (deleteTyped !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
                try {
                  await api.delete(`/pbos/${id}`);
                  toast.success("PBO permanently deleted");
                  navigate("/pbos", { replace: true });
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
              className="bg-[#8A1F1F] hover:bg-[#701818]"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#C9A961]/80">{label}</div>
      <div className="text-white/95 text-sm mt-0.5">{value || "—"}</div>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-sm text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
